
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TestActions } from "./TestActions";
import { formatDistanceToNow } from "date-fns";
import { MapPin, Users, Eye, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { useDuplicateTest } from "@/hooks/useDuplicateTest";

interface MobileTestCardProps {
  test: {
    id: string;
    title: string;
    description: string;
    location: string;
    created_at: string;
    status: string;
    response_count?: number;
    is_active?: boolean;
  };
  onDeleteTest: (testId: string) => void;
  onInviteCandidates: (testId: string) => void;
}

export const MobileTestCard = ({ test, onDeleteTest, onInviteCandidates }: MobileTestCardProps) => {
  const { duplicateTest, isDuplicating } = useDuplicateTest();
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100';
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="space-y-2">
          <div className="flex justify-between items-start gap-2">
            <Link 
              to={`/test/${test.id}`}
              className="block hover:text-primary transition-colors flex-1 min-w-0"
            >
              <h3 className="font-semibold text-base leading-tight text-foreground">
                {test.title}
              </h3>
            </Link>
            <div className="flex gap-1 flex-shrink-0">
              <Badge className={getStatusColor(test.status)} variant="outline">
                {test.status}
              </Badge>
              {!test.is_active && (
                <Badge variant="outline" className="text-xs">
                  Inactive
                </Badge>
              )}
            </div>
          </div>
          
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{test.location}</span>
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Users className="h-3 w-3 flex-shrink-0" />
              <span>{test.response_count || 0} responses</span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground line-clamp-2">
            {test.description}
          </p>
          
          <div className="text-xs text-muted-foreground">
            Created {formatDistanceToNow(new Date(test.created_at), { addSuffix: true })}
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <Link
              to={`/test/${test.id}`}
              className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <Eye className="h-3 w-3" />
              View Details
            </Link>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => duplicateTest(test.id)}
              disabled={isDuplicating}
              className="h-7 px-2"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          
          <TestActions
            jobId={test.id}
            jobDetails={{
              title: test.title,
              description: test.description,
              creator: { id: test.id }
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};
